import React, { useState } from 'react';
import TopicInput from '../components/TopicInput';
import ExplanationCard from '../components/ExplanationCard';
import QuizCard from '../components/QuizCard';
import { generateExplanation, generateQuiz, generateFeedback } from '../services/gemini';
import { saveLearningSession, saveQuizResult, auth } from '../services/firebase';

const Tutor = () => {
  const [topic, setTopic] = useState('');
  const [explanations, setExplanations] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('input');

  const handleTopicSubmit = async (topicText) => {
    setLoading(true);
    setTopic(topicText);
    setStep('explanation');

    try {
      const explanationData = await generateExplanation(topicText);
      setExplanations(explanationData);
      setLoading(false);
    } catch (error) {
      console.error('Error generating explanation:', error);
      setLoading(false);
    }
  };

  const handleStartQuiz = async () => {
    setLoading(true);
    setStep('quiz');

    try {
      const quizData = await generateQuiz(topic);
      setQuiz(quizData);
      setLoading(false);
    } catch (error) {
      console.error('Error generating quiz:', error);
      setLoading(false);
    }
  };

  const handleQuizSubmit = async (answers, score) => {
    setLoading(true);

    try {
      const userId = auth.currentUser?.uid;
      if (userId) {
        await saveQuizResult(userId, {
          topic,
          score,
          answers,
          totalQuestions: quiz.questions.length,
        });
      }

      const feedbackText = await generateFeedback(topic, score, answers);
      setFeedback(feedbackText);
      setStep('feedback');
      setLoading(false);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      setLoading(false);
    }
  };

  const handleReset = () => {
    setTopic('');
    setExplanations(null);
    setQuiz(null);
    setFeedback('');
    setStep('input');
  };

  return (
    <div className="container-custom py-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold mb-2">AI Tutor</h1>
          <p className="text-slate-400">Learn anything with personalized AI explanations</p>
        </div>

        {step === 'input' && (
          <TopicInput onSubmit={handleTopicSubmit} loading={loading} />
        )}

        {step === 'explanation' && (
          <div className="space-y-6 fade-in">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold">Understanding: {topic}</h2>
              <button onClick={handleReset} className="btn-outline text-sm">
                New Topic
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : (
              <>
                <ExplanationCard explanations={explanations} topic={topic} />
                <div className="text-center">
                  <button onClick={handleStartQuiz} className="btn-primary">
                    Test Your Knowledge 📝
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {step === 'quiz' && (
          <div className="space-y-6 fade-in">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold">Quiz: {topic}</h2>
              <button onClick={handleReset} className="btn-outline text-sm">
                New Topic
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : (
              <QuizCard quiz={quiz} onSubmit={handleQuizSubmit} />
            )}
          </div>
        )}

        {step === 'feedback' && (
          <div className="space-y-6 fade-in">
            <div className="card">
              <h2 className="text-2xl font-semibold mb-4">Personalized Feedback</h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-slate-300 whitespace-pre-wrap">{feedback}</p>
              </div>
            </div>

            <div className="flex gap-4 justify-center">
              <button onClick={handleReset} className="btn-primary">
                Learn Another Topic
              </button>
              <button onClick={() => setStep('explanation')} className="btn-outline">
                Review Explanation
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tutor;